import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { MODAL_TYPES } from '../../common/constants'
import i18n from '../../i18n'
import { setAccordionPage } from '../../store/accordionPagination'
import { hideModal } from '../../store/modal'
import { mapValuesToSelectOptions } from '../../utils/formUtils/forms'
import { PageListParams } from '../globalTypes'
import { invalidateQueries, updateToast } from '../helpers'
import { createActivity, deleteActivity, fetchActivityValues, getActivities, getActivityById, updateActivity } from './functions'
import {
    Activity,
    ActivityCreatePayload,
    ActivityCriteria,
    ActivityDeleteResponse,
    ActivityListResponse,
    ActivityMutationResponse,
    ActivityUpdatePayload,
    ActivityValuesData,
} from './types'

// TODO: GetActivities/GetActivity accept a generic Omit<UseQueryOptions<...>, 'queryKey'|'queryFn'>
// options bag (standard RQ pattern for reusable query hooks), but in practice every call site across
// the codebase only ever passes `enabled` through it. Consider whether a plain `enabled: boolean` param
// would be simpler for callers, at the cost of losing the ability to override staleTime/select/retry/etc.
// without changing the hook signature again. This is a project-wide pattern (every domain's hooks.ts
// does this), not specific to activities - revisit in its own ticket if worth doing.
const activities = {
    GetActivities: {
        useQuery: (
            params: PageListParams<ActivityCriteria>,
            options?: Omit<UseQueryOptions<ActivityListResponse, AxiosError>, 'queryKey' | 'queryFn'>
        ) =>
            useQuery({
                queryKey: ['activities', 'list', params],
                queryFn: () => getActivities(params),
                staleTime: 1 * 60 * 1000,
                ...options,
                enabled: options?.enabled ?? true,
            }),
    },
    GetActivity: {
        useQuery: (id: number, options?: Omit<UseQueryOptions<Activity, AxiosError>, 'queryKey' | 'queryFn'>) =>
            useQuery({
                queryKey: ['activities', 'detail', id],
                queryFn: () => getActivityById(id),
                staleTime: 1 * 60 * 1000,
                ...options,
                enabled: options?.enabled ?? true,
            }),
    },
    CreateActivity: {
        useMutation: (options?: UseMutationOptions<ActivityMutationResponse, AxiosError, ActivityCreatePayload>) => {
            const dispatch = useDispatch()

            return useMutation({
                onMutate: () => toast.loading(i18n.t('toast.loading') as string),
                mutationFn: (data: ActivityCreatePayload) => createActivity(data),
                onSuccess: (_data, _variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                    // Jump back to page 1 when new activity is added
                    dispatch(setAccordionPage('activities', 1))
                    dispatch(hideModal({ modalType: MODAL_TYPES.ACTIVITY_MODAL }))

                    invalidateQueries(['activities', 'list'])
                    invalidateQueries(['contacts', 'list'])
                },
                onError: (_error, _variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    UpdateActivity: {
        useMutation: (options?: UseMutationOptions<ActivityMutationResponse, AxiosError, ActivityUpdatePayload>) => {
            const dispatch = useDispatch()

            return useMutation({
                onMutate: () => toast.loading(i18n.t('toast.loading') as string),
                mutationFn: (data: ActivityUpdatePayload) => updateActivity(data),
                onSuccess: (_data, variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.success') as string, 'success')

                    dispatch(hideModal({ modalType: MODAL_TYPES.ACTIVITY_MODAL }))

                    invalidateQueries(['activities', 'list'])
                    invalidateQueries(['activities', 'detail', variables.id])
                },
                onError: (_error, _variables, context) => {
                    const toastId = context as number
                    toastId && updateToast(toastId, i18n.t('toast.update.error') as string, 'error')
                },
                ...options,
            })
        },
    },
    DeleteActivity: {
        useMutation: (options?: UseMutationOptions<ActivityDeleteResponse, AxiosError, number>) =>
            useMutation({
                mutationFn: (id: number) => deleteActivity(id),
                onSuccess: () => invalidateQueries(['activities', 'list']),
                ...options,
            }),
    },
    FormValues: {
        useQuery: (options?: Omit<UseQueryOptions<ActivityValuesData, AxiosError>, 'queryKey' | 'queryFn'>) => {
            const query = useQuery({
                queryKey: ['activityValues'],
                queryFn: () => fetchActivityValues(),
                staleTime: Infinity,
                gcTime: Infinity,
                ...options,
            })

            const formattedData = useMemo(() => mapValuesToSelectOptions(query.data), [query.data])

            return {
                ...query,
                data: formattedData,
                rawData: query.data,
            }
        },
    },
}

export default activities
